/**
 * WaypointTrackingService - GPS-Based Waypoint Notifications + Voice Confirmation
 * Phase 24: Real-time GPS tracking with proximity-triggered notifications
 * 
 * The Core Loop:
 * GPS position tracked in background →
 * 2 miles before known waypoint (rough road, fuel stop, wind corridor) →
 * Local notification fires with voice-actionable buttons →
 * User says "Yes" or "No" (Android notification voice actions) →
 * Report submitted to community database →
 * Next driver gets better information
 * 
 * This service is a no-op when running in a regular browser.
 * Only activates inside the Capacitor native app.
 */

import { authFetch } from './authFetch.js';
import * as units from './units.js';

const API_URL = import.meta.env?.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

// Notification type definitions with voice-actionable responses
const NOTIFICATION_TYPES = {
    rough_road: {
        title: 'Rough road ahead',
        body: (wp) => `${wp.highway || 'Road'} near ${wp.location || 'upcoming'} — still rough?`,
        actions: [
            { id: 'yes', title: 'Yes, still rough' },
            { id: 'no', title: 'No, it\'s been fixed' }
        ]
    },
    fuel_stop: {
        title: 'Fuel stop ahead',
        body: (wp) => `${wp.name || 'Fuel stop'} in ${units.distanceText(wp.distanceMiles || '2', { long: true })} — diesel available?`,
        actions: [
            { id: 'yes', title: 'Yes, diesel available' },
            { id: 'no', title: 'No, diesel unavailable' }
        ]
    },
    wind_corridor: {
        title: 'Wind corridor ahead',
        body: (wp) => `${wp.highway || 'Road'} — wind picking up as expected?`,
        actions: [
            { id: 'yes', title: 'Yes, very windy' },
            { id: 'light', title: 'Lighter than expected' },
            { id: 'no', title: 'No wind' }
        ]
    }
};

// Safety constants
const ALERT_DISTANCE_MILES = 2;
const MIN_NOTIFICATION_GAP_MS = 30000; // 30 seconds between notifications
const MAX_TRACKING_HOURS = 24;

class WaypointTrackingService {
    constructor() {
        this.isTracking = false;
        this.watchId = null;
        this.activeRoute = null;
        this.notifiedWaypoints = new Set();
        this.lastNotificationTime = 0;
        this.trackingStartTime = null;
        this.userId = null;
        this.listeners = [];
    }

    /**
     * Check if running inside Capacitor native shell
     */
    isCapacitorAvailable() {
        return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    }

    /**
     * Start GPS tracking for a launched route.
     * Call this when user taps Navigate with Zephyr.
     * 
     * @param {Object} route - The route object with waypoints
     * @param {string} userId - Authenticated user ID
     */
    async startRouteTracking(route, userId) {
        if (!this.isCapacitorAvailable()) {
            console.log('📍 Waypoint tracking: not in native app, skipping');
            return;
        }

        if (this.isTracking) {
            console.log('📍 Already tracking — stopping previous session');
            await this.stopTracking();
        }

        this.userId = userId;
        this.activeRoute = route;
        this.notifiedWaypoints = new Set();
        this.trackingStartTime = Date.now();
        this.isTracking = true;

        console.log('📍 Starting waypoint tracking for route');

        try {
            const Geolocation = window.Capacitor.Plugins.Geolocation;
            const LocalNotifications = window.Capacitor.Plugins.LocalNotifications;

            if (!Geolocation || !LocalNotifications) {
                console.warn('📍 Required plugins not available');
                this.isTracking = false;
                return;
            }

            // Register notification action types for voice responses
            await this._registerActionTypes(LocalNotifications);

            // Listen for notification action responses (voice or tap)
            this._addListener(
                await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
                    this._handleNotificationResponse(action);
                })
            );

            // Start GPS watch
            this.watchId = await Geolocation.watchPosition(
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
                (position, err) => {
                    if (err) {
                        console.warn('📍 GPS error:', err);
                        return;
                    }
                    if (position) {
                        this._onPositionUpdate(position.coords.latitude, position.coords.longitude);
                    }
                }
            );

            console.log('📍 GPS watch started, watchId:', this.watchId);

        } catch (error) {
            console.error('📍 Failed to start tracking:', error);
            this.isTracking = false;
        }
    }

    /**
     * Stop GPS tracking. Call when trip is complete or after 24 hours.
     */
    async stopTracking() {
        if (!this.isTracking) return;

        console.log('📍 Stopping waypoint tracking');

        try {
            const Geolocation = window.Capacitor.Plugins.Geolocation;
            if (Geolocation && this.watchId !== null) {
                await Geolocation.clearWatch({ id: this.watchId });
            }
        } catch (e) {
            console.warn('📍 Error clearing GPS watch:', e);
        }

        // Clean up listeners
        for (const listener of this.listeners) {
            if (listener && listener.remove) {
                await listener.remove();
            }
        }

        this.isTracking = false;
        this.watchId = null;
        this.activeRoute = null;
        this.notifiedWaypoints = new Set();
        this.listeners = [];
        console.log('📍 Tracking stopped');
    }

    /**
     * Handle GPS position update — check proximity to waypoints
     */
    _onPositionUpdate(lat, lng) {
        if (!this.activeRoute || !this.activeRoute.waypoints) return;

        // Auto-stop after 24 hours
        if (Date.now() - this.trackingStartTime > MAX_TRACKING_HOURS * 60 * 60 * 1000) {
            console.log('📍 Max tracking time reached — stopping');
            this.stopTracking();
            return;
        }

        this._checkUpcomingWaypoints(lat, lng);
    }

    /**
     * Check if any waypoints are within alert distance
     */
    _checkUpcomingWaypoints(lat, lng) {
        const now = Date.now();

        for (const wp of this.activeRoute.waypoints) {
            // Skip already-notified waypoints
            if (this.notifiedWaypoints.has(wp.id)) continue;

            // Skip waypoints without a notification type
            if (!wp.notificationType) continue;

            const distance = this._haversineDistance({ lat, lng }, { lat: wp.lat, lng: wp.lng });

            if (distance <= ALERT_DISTANCE_MILES) {
                // Enforce minimum gap between notifications
                if (now - this.lastNotificationTime < MIN_NOTIFICATION_GAP_MS) {
                    console.log('📍 Waypoint in range but too soon after last notification');
                    return; // Will catch it on next position update
                }

                this._sendWaypointNotification(wp);
                this.notifiedWaypoints.add(wp.id);
                this.lastNotificationTime = now;
            }
        }
    }

    /**
     * Send a local notification with voice-actionable buttons
     */
    async _sendWaypointNotification(waypoint) {
        const LocalNotifications = window.Capacitor.Plugins.LocalNotifications;
        if (!LocalNotifications) return;

        const type = NOTIFICATION_TYPES[waypoint.notificationType];
        if (!type) {
            console.warn('📍 Unknown notification type:', waypoint.notificationType);
            return;
        }

        const notificationId = Math.floor(Math.random() * 100000);

        try {
            await LocalNotifications.schedule({
                notifications: [{
                    title: type.title,
                    body: type.body(waypoint),
                    id: notificationId,
                    actionTypeId: waypoint.notificationType,
                    extra: {
                        waypointId: waypoint.id,
                        type: waypoint.notificationType,
                        highway: waypoint.highway || '',
                        location: waypoint.location || ''
                    }
                }]
            });

            console.log(`📍 Notification sent: ${type.title} for waypoint ${waypoint.id}`);
        } catch (error) {
            console.error('📍 Failed to send notification:', error);
        }
    }

    /**
     * Register notification action types — these become voice-triggerable on Android
     * via Google Assistant when the notification is active.
     */
    async _registerActionTypes(LocalNotifications) {
        try {
            await LocalNotifications.registerActionTypes({
                types: Object.entries(NOTIFICATION_TYPES).map(([id, type]) => ({
                    id,
                    actions: type.actions
                }))
            });
            console.log('📍 Notification action types registered');
        } catch (error) {
            console.error('📍 Failed to register action types:', error);
        }
    }

    /**
     * Handle notification response — fired when user taps or says an action
     */
    async _handleNotificationResponse(action) {
        const extra = action.notification?.extra || {};
        const { waypointId, type } = extra;
        const actionId = action.actionId;

        if (!waypointId || !type || !actionId) {
            console.warn('📍 Incomplete notification response:', action);
            return;
        }

        console.log(`📍 Waypoint report: ${type} → ${actionId} (waypoint: ${waypointId})`);

        await this._submitWaypointReport(waypointId, type, actionId);
    }

    /**
     * Submit waypoint report to backend
     */
    async _submitWaypointReport(waypointId, type, response) {
        // Map response to community data update
        const reportMap = {
            rough_road: {
                yes: { confirmed: true },
                no: { confirmed: false, fixedAt: new Date().toISOString() }
            },
            fuel_stop: {
                yes: { dieselAvailable: true },
                no: { dieselAvailable: false }
            },
            wind_corridor: {
                yes: { windLevel: 'high' },
                light: { windLevel: 'light' },
                no: { windLevel: 'none' }
            }
        };

        const report = reportMap[type]?.[response];
        if (!report) {
            console.warn('📍 Unknown response mapping:', type, response);
            return;
        }

        try {
            const res = await authFetch(`${API_URL}/api/waypoint-reports`, {
                method: 'POST',
                body: JSON.stringify({
                    userId: this.userId,
                    waypointId,
                    type,
                    response,
                    report,
                    source: 'notification_action',
                    reportedAt: new Date().toISOString()
                })
            });

            if (res.ok) {
                console.log('📍 Waypoint report submitted successfully');
            } else {
                console.warn('📍 Waypoint report failed:', res.status);
            }
        } catch (error) {
            console.error('📍 Error submitting waypoint report:', error);
        }
    }

    /**
     * Haversine distance between two points in miles
     */
    _haversineDistance(point1, point2) {
        const R = 3959; // Earth radius in miles
        const dLat = this._toRad(point2.lat - point1.lat);
        const dLng = this._toRad(point2.lng - point1.lng);
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(this._toRad(point1.lat)) * Math.cos(this._toRad(point2.lat)) *
            Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    _toRad(deg) {
        return deg * (Math.PI / 180);
    }

    _addListener(listener) {
        if (listener) this.listeners.push(listener);
    }

    /**
     * Check if tracking is currently active
     */
    isActive() {
        return this.isTracking;
    }
}

// Export singleton
export const waypointTrackingService = new WaypointTrackingService();
